import { FunctionComponent, useState } from "react";
import { FieldErrors, UseFormSetValue } from "react-hook-form";

import {
	LinkedinPersonSuggestion,
	LinkedinPersonSuggestionItem,
} from "@/components/LinkedinPersonSuggestion/LinkedinPersonSuggestion";
import {
	LinkedinOrganizationSuggestion,
	LinkedinOrganizationSuggestionItem,
} from "@/components/LinkedinOrganizationSuggestion/LinkedinOrganizationSuggestion";
import {
	AutoComplete,
	AutoCompleteItem as AutoCompleteItemType,
} from "@/components/AutoComplete/AutoComplete";

import { CreateLiPost } from "../schema/liPost";
import { LinkedinSuggestions } from "./useLinkedinSuggestions";
import { useEnrichedProfileSuggestions } from "./useEnrichedProfileSuggestions";

interface ProfileSourceSectionProps {
	formValues: Partial<CreateLiPost>;
	setValue: UseFormSetValue<CreateLiPost>;
	errors: FieldErrors<CreateLiPost>;
	suggestions: LinkedinSuggestions;
}

const RESOLVE_ERROR_MESSAGE = "Could not resolve the LinkedIn profile. Please try another one.";

/**
 * BY_PROFILE source inputs: tracked person, tracked company, and the scraping user.
 *
 * The person/company autocompletes run on CompanyEnrich; a picked suggestion is resolved
 * to its LinkedIn urn before it is written to the form (the post scraper needs the urn).
 */
export const ProfileSourceSection: FunctionComponent<ProfileSourceSectionProps> = ({
	formValues,
	setValue,
	errors,
	suggestions,
}) => {
	const { profiles, setProfileSearchValue } = suggestions;

	const {
		personSuggestions,
		setPersonSuggestions,
		setPersonSearchValue,
		isLoadingPeople,
		organizationSuggestions,
		setOrganizationSuggestions,
		setCompanySearchValue,
		isLoadingCompanies,
		resolvePerson,
		resolveCompany,
		isResolvingPerson,
		isResolvingCompany,
	} = useEnrichedProfileSuggestions();

	const [personResolveError, setPersonResolveError] = useState(false);
	const [companyResolveError, setCompanyResolveError] = useState(false);

	const profileFullName = (id?: number | null) => {
		const profile = profiles?.data.find((p) => p.id === id);

		return profile ? `${profile.firstName} ${profile.lastName}` : "";
	};

	const handleSelectPerson = async (value?: LinkedinPersonSuggestionItem) => {
		setPersonSuggestions([]);
		setPersonSearchValue("");
		setPersonResolveError(false);

		if (!value) {
			setValue("fromMember", null, { shouldDirty: true, shouldValidate: true });

			return;
		}

		try {
			const resolved = await resolvePerson(value.urn);

			setValue("fromMember", resolved, { shouldDirty: true, shouldValidate: true });
		} catch {
			setPersonResolveError(true);
		}
	};

	const handleSelectCompany = async (value?: LinkedinOrganizationSuggestionItem) => {
		setOrganizationSuggestions([]);
		setCompanySearchValue("");
		setCompanyResolveError(false);

		if (!value) {
			setValue("fromOrganization", null, { shouldDirty: true, shouldValidate: true });

			return;
		}

		try {
			const resolved = await resolveCompany(value.urn);

			setValue("fromOrganization", resolved, { shouldDirty: true, shouldValidate: true });
		} catch {
			setCompanyResolveError(true);
		}
	};

	return (
		<>
			<div className="flex flex-col w-full mb-4">
				<label className="text-sm mb-1">Add personal profile</label>
				<span className="text-xs text-gray-700 mb-2">
					Search by name or paste a linkedin.com/in/… URL
				</span>
				<LinkedinPersonSuggestion
					data={personSuggestions || []}
					floating={true}
					handleSearch={(val) => setPersonSearchValue(val)}
					handleSelect={handleSelectPerson}
					isDisabled={!!formValues?.fromOrganization || isResolvingPerson}
					isLoading={isLoadingPeople || isResolvingPerson}
					selectedItem={(formValues.fromMember as LinkedinPersonSuggestionItem) || undefined}
					showError={!!errors.fromMember?.message || personResolveError}
				/>
				{personResolveError && (
					<p className="text-xs text-status-error mt-1">{RESOLVE_ERROR_MESSAGE}</p>
				)}
			</div>

			<div className="flex flex-col w-full mb-4">
				<label className="text-sm mb-1">Add company profile</label>
				<span className="text-xs text-gray-700 mb-2">
					Search by name or paste a linkedin.com/company/… URL
				</span>
				<LinkedinOrganizationSuggestion
					data={organizationSuggestions || []}
					floating={true}
					handleSearch={(val) => setCompanySearchValue(val)}
					handleSelect={handleSelectCompany}
					isDisabled={!!formValues?.fromMember || isResolvingCompany}
					isLoading={isLoadingCompanies || isResolvingCompany}
					selectedItem={
						(formValues.fromOrganization as LinkedinOrganizationSuggestionItem) || undefined
					}
					showError={!!errors.fromOrganization?.message || companyResolveError}
				/>
				{companyResolveError && (
					<p className="text-xs text-status-error mt-1">{RESOLVE_ERROR_MESSAGE}</p>
				)}
			</div>

			{(formValues.commentsToComment || formValues.likesToComment) && (
				<div className="flex flex-row w-full mb-4">
					<AutoComplete
						data={
							profiles?.data?.map(
								(c) =>
									({
										id: c.id,
										title: c.firstName + " " + c.lastName,
									}) as AutoCompleteItemType
							) || []
						}
						handleSearch={(value: string) => setProfileSearchValue(value)}
						handleSelect={(item?: AutoCompleteItemType) => {
							setValue("plannedScrapingUserId", item?.id ? Number(item.id) : null, {
								shouldDirty: true,
								shouldValidate: true,
							});
						}}
						information="Select a user to scrape the comments or likes of the added profile. Leave empty for random profile. The user needs to be logged in with linkedin."
						label={"Select scraping signalist user"}
						placeholder="Leave empty for random profile"
						selectedItem={profileFullName(formValues.plannedScrapingUserId)}
					/>
				</div>
			)}
		</>
	);
};
